import React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import DOMPurify from 'dompurify';

// Стилизованный контейнер для отображения богатого контента
const StyledContentContainer = styled(Box)(({ theme }) => ({
  // Общие стили для контейнера
  width: '100%',
  maxWidth: '100%',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  
  // Критически важно: предотвращаем отображение HTML тегов как текста
  '& *': {
    boxSizing: 'border-box',
  },
  
  // Мобильные оптимизации
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: 1.6,
    padding: theme.spacing(0, 1),
  },
  
  // Основные стили для заголовков
  '& h1': {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(4),
    color: theme.palette.primary.main,
    lineHeight: 1.2,
    '&:first-of-type': {
      marginTop: 0,
    },
    // Мобильные стили
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
  },
  '& h2': {
    fontSize: '2rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    color: theme.palette.primary.main,
    lineHeight: 1.3,
    '&:first-of-type': {
      marginTop: 0,
    },
    // Мобильные стили
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.75rem',
      marginBottom: theme.spacing(1.5),
      marginTop: theme.spacing(2.5),
    },
  },
  '& h3': {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    color: theme.palette.primary.main,
    lineHeight: 1.4,
    '&:first-of-type': {
      marginTop: 0,
    },
    // Мобильные стили
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
  },
  '& h4': {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  '& h5': {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.primary,
  },
  '& h6': {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.primary,
  },
  '& p': {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
    maxWidth: '100%',
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
    '&:last-child': {
      marginBottom: 0,
    },
    // Мобильные стили
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      lineHeight: 1.7,
      marginBottom: theme.spacing(1.5),
    },
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(3),
    margin: theme.spacing(3, 0),
    fontStyle: 'italic',
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(2, 3),
    borderRadius: theme.shape.borderRadius,
    fontSize: '1.1rem',
    '& p': {
      marginBottom: theme.spacing(1),
      '&:last-child': {
        marginBottom: 0,
      },
    },
  },
  '& ul, & ol': {
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.7,
      fontSize: '1.1rem',
      color: theme.palette.text.primary,
      '& ul, & ol': {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
      },
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    margin: theme.spacing(2, 0),
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    // Мобильные стили
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1.5, 0),
      borderRadius: theme.shape.borderRadius / 2,
      boxShadow: theme.shadows[2],
    },
  },
  '& pre': {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    fontSize: '14px',
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(2, 0),
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
      borderRadius: 0,
      fontSize: 'inherit',
    },
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.25, 0.75),
    borderRadius: '4px',
    fontSize: '0.9em',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    border: `1px solid ${theme.palette.divider}`,
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    margin: theme.spacing(2, 0),
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.action.hover,
      fontWeight: 600,
    },
  },
  '& hr': {
    border: 'none',
    height: '2px',
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(3, 0),
    borderRadius: '1px',
  },
  // Стили для выделения текста
  '& strong': {
    fontWeight: 700,
  },
  '& em': {
    fontStyle: 'italic',
  },
  '& u': {
    textDecoration: 'underline',
  },
  '& s': {
    textDecoration: 'line-through',
  },
  // Стили для видео
  '& iframe': {
    maxWidth: '100%',
    margin: theme.spacing(2, 0),
    borderRadius: theme.shape.borderRadius,
  },
  // Стили для выравнивания текста
  '& .ql-align-left, & p.ql-align-left, & h1.ql-align-left, & h2.ql-align-left, & h3.ql-align-left, & h4.ql-align-left, & h5.ql-align-left, & h6.ql-align-left': {
    textAlign: 'left !important',
  },
  '& .ql-align-center, & p.ql-align-center, & h1.ql-align-center, & h2.ql-align-center, & h3.ql-align-center, & h4.ql-align-center, & h5.ql-align-center, & h6.ql-align-center': {
    textAlign: 'center !important',
  },
  '& .ql-align-right, & p.ql-align-right, & h1.ql-align-right, & h2.ql-align-right, & h3.ql-align-right, & h4.ql-align-right, & h5.ql-align-right, & h6.ql-align-right': {
    textAlign: 'right !important',
  },
  '& .ql-align-justify, & p.ql-align-justify, & h1.ql-align-justify, & h2.ql-align-justify, & h3.ql-align-justify, & h4.ql-align-justify, & h5.ql-align-justify, & h6.ql-align-justify': {
    textAlign: 'justify !important',
  },
  
  // Стили для информационных блоков (callouts)
  '& .callout': {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
    '& .callout-title': {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      fontSize: '1.1rem',
    },
    '& .callout-content': {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    // Мобильные стили
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      margin: theme.spacing(1.5, 0),
      '& .callout-title': {
        fontSize: '1rem',
      },
      '& .callout-content': {
        fontSize: '0.9rem',
      },
    },
  },
  
  // Стили для стилизованного текста
  '& .styled-text': {
    display: 'inline',
  },
  
  // Общие мобильные оптимизации
  [theme.breakpoints.down('sm')]: {
    '& *': {
      maxWidth: '100% !important',
      overflowWrap: 'break-word !important',
      wordWrap: 'break-word !important',
    },
  },
}));

const RichTextDisplay = ({ content, className, ...props }) => {
  // Проверяем наличие контента
  if (!content || typeof content !== 'string') {
    return null;
  }

  // Очищаем HTML от потенциально опасного контента
  const sanitizedContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'strong', 'em', 'u', 's',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'img', 'iframe',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'hr', 'div', 'span'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'width', 'height',
      'style', 'class', 'target', 'rel',
      'frameborder', 'allowfullscreen'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    // Дополнительные настройки для мобильных устройств
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
  });

  // Проверяем, что контент не пустой после санитарии
  if (!sanitizedContent || sanitizedContent.trim() === '') {
    return null;
  }

  return (
    <StyledContentContainer
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      {...props}
    />
  );
};

export default RichTextDisplay;
